import { batch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { handleErrors } from '../../actions/ErrorActions';
import { addSuccess } from '../../actions/SuccessActions';
import {
  addWarning,
  clearWarnings,
  enableWarnings,
  removeWarningByIdAndMessage,
  updateAllWarnings,
} from '../../actions/WarningActions';
import {
  deleteEntity as deleteEntityAPI,
  importEntity as importEntityAPI,
  publishEntity,
  recreateTemplates as recreateTemplatesAPI,
  saveEntity,
} from '../../api/entity';
import { getSavedSearchResultsForAutocomplete } from '../../api/saved-searches';
import { AUTOCOMPLETE_DROPDOWN_SIZE } from '../../constants/SizeConstants';
import { WARNING_SOURCES } from '../../constants/WarningsConstants';
import {
  NON_EXISTENT_ENTITY_VERSION,
  RESOLVED_FIELDS_SOURCE,
  RESOLVED_FIELDS_TARGET,
  TAB_FIELDS_INDEX,
} from '../constants/entity-designer.constants';
import { getIsPilgrimEntity, modifyEntityModel } from '../utils/entity-designer.utils';
import { getRightValidationErrorMessage, validateEntityOnSave } from '../utils/validation.utils';

export const EDIT_ENTITY_INFO = 'EDIT_ENTITY_INFO';
export const LOAD_ENTITY = 'LOAD_ENTITY';
export const SET_SELECTED_FIELD_SET = 'SET_SELECTED_FIELD_SET';
export const CLEAR_ENTITY_EDITOR = 'CLEAR_ENTITY_EDITOR';

export const ADD_ENTITY_FIELD = 'ADD_ENTITY_FIELD';
export const EDIT_ENTITY_FIELD = 'EDIT_ENTITY_FIELD';
export const DELETE_ENTITY_FIELD = 'DELETE_ENTITY_FIELD';

export const ADD_ENTITY_RELATION = 'ADD_ENTITY_RELATION';
export const EDIT_ENTITY_RELATION = 'EDIT_ENTITY_RELATION';
export const DELETE_ENTITY_RELATION = 'DELETE_ENTITY_RELATION';

export const ADD_RESOLVED_FIELD = 'ADD_RESOLVED_FIELD';
export const EDIT_RESOLVED_FIELD = 'EDIT_RESOLVED_FIELD';
export const DELETE_RESOLVED_FIELD = 'DELETE_RESOLVED_FIELD';
export const LINK_RESOLVED_FIELD = 'LINK_RESOLVED_FIELD';
export const UNLINK_RESOLVED_FIELD = 'UNLINK_RESOLVED_FIELD';
export const ADD_VALIDATION_ERRORS = 'ADD_VALIDATION_ERRORS';
export const DELETE_VALIDATION_ERRORS = 'DELETE_VALIDATION_ERRORS';

export const ADD_AUTOCOMPLETE_ERROR = 'ADD_AUTOCOMPLETE_ERROR';
export const DELETE_AUTOCOMPLETE_ERROR = 'DELETE_AUTOCOMPLETE_ERROR';

export const SET_IMPORT_MODE = 'SET_IMPORT_MODE';

export const loadEntity = (data) => ({
  type: LOAD_ENTITY,
  payload: data,
});

export const setImportMode = (data) => ({
  type: SET_IMPORT_MODE,
  payload: data,
});

export const clearEntityEditor = () => ({
  type: CLEAR_ENTITY_EDITOR,
});

export const setSelectedFieldSet = (data) => ({
  type: SET_SELECTED_FIELD_SET,
  payload: data,
});

export const editEntityInfo = (data) => ({
  type: EDIT_ENTITY_INFO,
  payload: data,
});

export const addEntityField = (data) => ({
  type: ADD_ENTITY_FIELD,
  payload: data,
});

export const editEntityField = (data) => ({
  type: EDIT_ENTITY_FIELD,
  payload: data,
});

export const deleteEntityField = (data) => ({
  type: DELETE_ENTITY_FIELD,
  payload: data,
});

export const addEntityRelation = (data) => ({
  type: ADD_ENTITY_RELATION,
  payload: data,
});

export const editEntityRelation = (data) => ({
  type: EDIT_ENTITY_RELATION,
  payload: data,
});

export const deleteEntityRelation = (data) => ({
  type: DELETE_ENTITY_RELATION,
  payload: data,
});

export const addResolvedField = (data) => ({
  type: ADD_RESOLVED_FIELD,
  payload: data,
});

export const editResolvedField = (data) => ({
  type: EDIT_RESOLVED_FIELD,
  payload: data,
});

export const deleteResolvedField = (data) => ({
  type: DELETE_RESOLVED_FIELD,
  payload: data,
});

export const linkResolvedField = (data) => ({
  type: LINK_RESOLVED_FIELD,
  payload: data,
});

export const unlinkResolvedField = (data) => ({
  type: UNLINK_RESOLVED_FIELD,
  payload: data,
});

export const addValidationErrors = (data) => ({
  type: ADD_VALIDATION_ERRORS,
  payload: data,
});

export const deleteValidationErrors = () => ({
  type: DELETE_VALIDATION_ERRORS,
});

export const addAutocompleteError = (data) => ({
  type: ADD_AUTOCOMPLETE_ERROR,
  payload: data,
});

export const deleteAutocompleteError = (data) => ({
  type: DELETE_AUTOCOMPLETE_ERROR,
  payload: data,
});

export const validateEntityDesigner = () => (dispatch, getState) => {
  try {
    const { entityModel, resolvedFields } = getState().entityEditor;
    const { translations } = getState();

    const {
      EntityInfo: { RelatedEntities: relations, RuntimeTable: runtimeTableValue },
      EntityFields: fields,
    } = entityModel;

    dispatch(clearWarnings());
    dispatch(deleteValidationErrors());

    const validationErrors = validateEntityOnSave({
      fields,
      relations,
      resolvedFields,
      translations,
      runtimeTableValue,
    });

    if (validationErrors.length) {
      dispatch(addValidationErrors(validationErrors));
      dispatch(updateAllWarnings(validationErrors));
      dispatch(enableWarnings());
    }
  } catch (error) {
    throw new Error(`validateEntityDesigner() failed: ${error}`);
  }
};

export const saveEntityDesigner = ({
  setIsLoading,
  setSelectedTab,
  redirectToAnotherEntity,
}) => async (dispatch, getState) => {
  try {
    dispatch(validateEntityDesigner());

    const {
      entityModel,
      validationErrors,
      autocompleteErrors,
      isImportMode,
    } = getState().entityEditor;

    if (validationErrors?.length || autocompleteErrors?.length) {
      if (autocompleteErrors?.length) {
        dispatch(updateAllWarnings(autocompleteErrors));
        dispatch(enableWarnings());
      }
      return;
    }

    setIsLoading(true);

    const response = await saveEntity({
      isPilgrim: false,
      data: entityModel,
      isNew: entityModel?.EntityInfo?.Version === NON_EXISTENT_ENTITY_VERSION,
    });

    if (!response.hasError) {
      const currentEntityId = entityModel.EntityInfo?.Id;
      const updatedEntityId = response.EntityModel?.EntityInfo?.Id;

      batch(() => {
        dispatch(addSuccess({ message: 'Task Saved Successfully' }));
        dispatch(loadEntity(modifyEntityModel(response.EntityModel)));
        dispatch(setSelectedFieldSet(''));
        isImportMode && dispatch(setImportMode(false));
      });

      setSelectedTab(TAB_FIELDS_INDEX);

      if (currentEntityId !== updatedEntityId || isImportMode) {
        redirectToAnotherEntity({
          itmId: updatedEntityId,
        });
      }
    } else if (Array.isArray(response.errors)) {
      dispatch(handleErrors(response.errors, { isTopBanner: true }));
    }
  } catch (error) {
    throw new Error(`saveEntityDesigner() failed: ${error}`);
  }
};

export const publishEntityDesigner = ({ redirectToPrevPage }) => async (dispatch, getState) => {
  try {
    const {
      entityModel: { EntityInfo },
    } = getState().entityEditor;
    const response = await publishEntity({
      entityId: EntityInfo?.Id,
      isPilgrim: getIsPilgrimEntity(EntityInfo),
    });

    if (!response.hasError) {
      dispatch(addSuccess({ message: 'Entity Published Successfully' }));
      redirectToPrevPage();
    } else if (Array.isArray(response.errors)) {
      dispatch(handleErrors(response.errors, { isTopBanner: true }));
    }
  } catch (error) {
    throw new Error(`publishEntityDesigner() failed: ${error}`);
  }
};

export const deleteEntity = ({ redirectToPrevPage }) => async (dispatch, getState) => {
  try {
    const {
      entityModel: { EntityInfo },
    } = getState().entityEditor;

    const response = await deleteEntityAPI({ entityId: EntityInfo.Id });

    if (!response.hasError) {
      dispatch(addSuccess({ message: 'Entity Deleted Successfully' }));
      redirectToPrevPage();
    } else if (Array.isArray(response.errors)) {
      dispatch(handleErrors(response.errors));
    }
  } catch (error) {
    throw new Error(`deleteEntity() failed: ${error}`);
  }
};

export const applyResolvedFieldsChanges = (params) => (dispatch, getState) => {
  try {
    const { resolvedFields } = getState().entityEditor;
    const prevResolvedField = resolvedFields?.find((field) => field.rowId === params.rowId);

    if (!prevResolvedField) {
      throw new Error('Resolved field is not found');
    }

    if (params.key === RESOLVED_FIELDS_TARGET) {
      batch(() => {
        prevResolvedField.target &&
          dispatch(unlinkResolvedField({ rowId: prevResolvedField.target }));

        dispatch(
          linkResolvedField({
            fieldRowId: params.value,
            fieldSet: params.fieldSet,
            source: prevResolvedField.source,
          })
        );
        dispatch(editResolvedField(params));
      });
      return;
    }
    if (params.key === RESOLVED_FIELDS_SOURCE) {
      batch(() => {
        dispatch(editResolvedField(params));

        prevResolvedField.target &&
          dispatch(
            editEntityField({
              rowId: prevResolvedField.target,
              key: 'ResolverFor',
              value: params.value,
            })
          );
      });
    }
  } catch (error) {
    throw new Error(`applyResolvedFieldsChanges() failed: ${error}`);
  }
};

export const autoCompleteRightValue = ({
  value,
  propName,
  valueToSelect,
  label,
  setLocalValue,
}) => {
  return async (dispatch, getState) => {
    const reduxState = getState();
    const translations = reduxState.translations;

    batch(() => {
      dispatch(deleteAutocompleteError({ instanceId: propName }));
      dispatch(
        removeWarningByIdAndMessage(propName, getRightValidationErrorMessage(label, translations))
      );
    });

    if (value === '') {
      dispatch(editEntityInfo({ prop: propName, value: '' }));
      setLocalValue && setLocalValue({ label: '', value: '' });
      return;
    }

    const filter = [
      {
        openGroup: false,
        fieldName: valueToSelect,
        sqlOperator: 'SW',
        value: value.trim(),
        closeGroup: false,
        relationalOp: 'AND',
      },
    ];

    const requestParams = {
      currentPageIndex: 0,
      dynamicParamCollection: {},
      entityName: 'V_RHT_RIGHT',
      filter,
      pageSize: AUTOCOMPLETE_DROPDOWN_SIZE,
      referencedColumns: [],
      savedSearchName: 'All Rights',
      sortExpression: '',
    };

    const response = await getSavedSearchResultsForAutocomplete(requestParams);
    const dataRows = response?.SearchData?.rows ?? [];

    if (!dataRows.length) {
      setLocalValue && setLocalValue({ label: value, value: value.trim() });
      batch(() => {
        dispatch(
          addAutocompleteError({
            errorId: uuidv4(),
            instanceId: propName,
            message: getRightValidationErrorMessage(label, translations),
            source: WARNING_SOURCES.AUTOCOMPLETE,
          })
        );
        dispatch(
          addWarning({
            instanceId: propName,
            source: WARNING_SOURCES.AUTOCOMPLETE,
            message: getRightValidationErrorMessage(label, translations),
          })
        );
        dispatch(editEntityInfo({ prop: propName, value: value.trim() }));
      });

      return;
    }

    const selectedDataValue = dataRows[0][valueToSelect];

    dispatch(editEntityInfo({ prop: propName, value: selectedDataValue.trim() }));
    setLocalValue && setLocalValue({ label: selectedDataValue, value: selectedDataValue.trim() });
  };
};

export const getAutoCompleteRightValues = ({ value, valueToSelect }) => {
  return async () => {
    const filter = [
      {
        openGroup: false,
        fieldName: valueToSelect,
        sqlOperator: 'SW',
        value,
        closeGroup: false,
        relationalOp: 'AND',
      },
    ];

    const requestParams = {
      currentPageIndex: 0,
      dynamicParamCollection: {},
      entityName: 'V_RHT_RIGHT',
      filter,
      pageSize: AUTOCOMPLETE_DROPDOWN_SIZE,
      referencedColumns: [],
      savedSearchName: 'All Rights',
      sortExpression: '',
    };

    const response = await getSavedSearchResultsForAutocomplete(requestParams);
    const dataRows = response?.SearchData?.rows ?? [];

    const autocompleteOption = dataRows.map((row) => ({
      label: row[valueToSelect],
      value: row[valueToSelect],
    }));
    return autocompleteOption;
  };
};

export const importEntity = ({ file, redirectToEntityDesigner }) => async (dispatch) => {
  const response = await importEntityAPI(file);

  if (response.hasError) {
    throw response.errors;
  } else {
    batch(() => {
      dispatch(addSuccess({ message: 'Entity Imported Successfully' }));
      dispatch(loadEntity(modifyEntityModel(response.EntityModel)));
      dispatch(setImportMode(true));
    });
    redirectToEntityDesigner();
  }
};

export const recreateTemplates = () => async (dispatch, getState) => {
  const { Id } = getState().entityEditor.entityModel.EntityInfo;

  const { hasError, errors } = await recreateTemplatesAPI({ entityId: Id });

  if (hasError) {
    dispatch(handleErrors(errors));
  } else {
    dispatch(addSuccess({ message: 'Templates recreated successfully' }));
  }
};
